import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Banner,
  SkeletonPage,
  SkeletonBodyText,
  Card,
  InlineStack,
  Badge,
} from '@shopify/polaris';
import { useSuggestions, useMarkSuggestionActioned } from '../hooks/useSuggestions';
import { SuggestionList } from '../components/SuggestionList';
import { BlurredSuggestion } from '../components/BlurredSuggestion';
import { UpgradeBanner } from '../components/UpgradeBanner';
import { PageEntrance, StaggeredList } from '../components/ui';
import './SuggestionsPage.css';

/**
 * T060: SuggestionsPage.
 * SuggestionList (first 3 visible), BlurredSuggestion (remaining),
 * UpgradeBanner, congratulatory message for score 95+ with zero suggestions.
 */

export function SuggestionsPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useSuggestions(id);
  const { mutate: markActioned, isPending } = useMarkSuggestionActioned(id);

  if (isLoading) {
    return (
      <SkeletonPage
        title="Suggestions"
        backAction
      >
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  if (error || !data) {
    return (
      <Page
        title="Suggestions"
        backAction={{ content: 'Product', onAction: () => navigate(`/products/${id}`) }}
      >
        <Banner tone="critical">
          <p>{error?.message ?? 'Unable to load suggestions.'}</p>
        </Banner>
      </Page>
    );
  }

  const { suggestions, totalCount, actionedCount } = data;
  const visibleSuggestions = suggestions.filter((s) => !s.isBlurred);
  const blurredCount = suggestions.filter((s) => s.isBlurred).length;

  // Congratulatory state — score 95+ with zero non-actioned suggestions
  const allActioned = suggestions.every((s) => s.isActioned);
  const hasSuggestions = totalCount > 0;

  return (
    <PageEntrance>
    <Page
      title="Fix Suggestions"
      backAction={{ content: 'Product', onAction: () => navigate(`/products/${id}`) }}
      subtitle={`${totalCount} suggestion${totalCount !== 1 ? 's' : ''} · ${actionedCount} resolved`}
    >
      <Layout>
        <Layout.Section>
          <StaggeredList>
            {/* Summary stats */}
            <InlineStack gap="400">
              <Badge tone="info">{`${totalCount} total`}</Badge>
              <Badge tone="success">{`${actionedCount} resolved`}</Badge>
              <Badge tone="warning">{`${totalCount - actionedCount} remaining`}</Badge>
            </InlineStack>

            {/* Congratulatory message */}
            {hasSuggestions && allActioned && (
              <Banner
                title="All suggestions resolved!"
                tone="success"
              >
                <p>
                  Great work! You&apos;ve addressed all the suggestions for this product.
                  Rescan to see your updated score.
                </p>
              </Banner>
            )}

            {!hasSuggestions && (
              <Banner
                title="No suggestions needed"
                tone="success"
              >
                <p>
                  This product looks great! No improvements needed at this time.
                </p>
              </Banner>
            )}

            {/* Visible suggestion cards */}
            <SuggestionList
              suggestions={visibleSuggestions}
              onAction={(suggestionId) => markActioned(suggestionId)}
              isActioning={isPending}
            />

            {/* Blurred suggestions for free tier */}
            {blurredCount > 0 && <BlurredSuggestion count={blurredCount} />}

            {/* Upgrade banner when there are blurred suggestions */}
            {blurredCount > 0 && (
              <UpgradeBanner
                visibleCount={visibleSuggestions.length}
                totalCount={totalCount}
                feature="suggestions"
              />
            )}
          </StaggeredList>
        </Layout.Section>
      </Layout>
    </Page>
    </PageEntrance>
  );
}
